import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Clock } from 'lucide-react';
import SmartImage from './SmartImage';
import StoreBadges from './StoreBadges';
import { blogPosts } from '../data/blogPosts';
import { normaliseCategory } from '../data/blogCategories';
import { SHOP_PRODUCTS, getProductEtsyUrl } from '../data/shopProducts';
import { HOME_PORTALS } from '../data/homePortals';
import { getCatalogProduct, mediaPath, productPath } from '../data/shopCatalog';
import { appUrl } from '../utils/appUrl';

/* HomeShowcase replaces the three homepage carousels (blog, health portals,
 * shop) and the "Discover Recalibrate" hub grid with one calm section.
 * The app sits on the left as the main action; guides, portals and articles
 * sit in one tabbed shelf on the right, each showing its whole set at once.
 * Nothing moves on its own, so the app stays the focus. */

// Every live product: the October catalog first (course, minis, journal, starter
// kit), then the three earlier field guides. Each card opens its product page.
const kindOf = (tag) => { const k = tag.split(' · ')[0].toLowerCase(); return k.charAt(0).toUpperCase() + k.slice(1); };
const ORDER = ['built-nervous-system-course', '3am-wake-up-guide', 'panic-attack-guide', 'sunday-scaries-guide', 'presentation-nerves-guide',
  'afternoon-slump-guide', 'after-an-argument-guide', 'anger-calm-down-guide', 'stop-overthinking-guide', 'window-of-tolerance-journal', 'nervous-system-reset-starter-kit'];
const GUIDES = [
  ...ORDER.map(getCatalogProduct).filter(Boolean).map((p) => ({ id: p.slug, title: p.name, cover: mediaPath(p, 'cover-600.webp'), kind: kindOf(p.tag), price: p.price, to: productPath(p) })),
  { ...SHOP_PRODUCTS['calming-nervous-system-ebook'], cover: '/shop-images/home-calming-nervous-system.webp', kind: 'Field guide' },
  { ...SHOP_PRODUCTS['30-day-nervous-system-reset'], cover: '/shop-images/home-30-day-reset.webp', kind: 'Habit calendar' },
  { ...SHOP_PRODUCTS['pocket-flashcards-vol-1'], cover: '/shop-images/home-pocket-flashcards.webp', kind: '50 cards' },
];

// Same ordering the blog page features: original covers first, then newest.
const hasOriginalCover = (post) => Boolean(
  post.image && !post.image.includes('/blog-images/cover-') && !post.image.includes('/blog-images/recalibrate-')
);
const ARTICLES = [...blogPosts]
  .filter((p) => p.image)
  .sort((a, b) => (Number(hasOriginalCover(b)) - Number(hasOriginalCover(a))) || (new Date(b.date) - new Date(a.date)))
  .slice(0, 4);

const TABS = [
  { key: 'guides', label: 'Guides', count: GUIDES.filter((g) => !g.comingSoon).length },
  { key: 'portals', label: 'Health portals', count: HOME_PORTALS.length },
  { key: 'articles', label: 'Articles', count: blogPosts.length },
];


export default function HomeShowcase() {
  const [tab, setTab] = useState('guides');
  const tabRefs = useRef({});

  const onKeyDown = (e) => {
    const i = TABS.findIndex((t) => t.key === tab);
    let next = null;
    if (e.key === 'ArrowRight') next = TABS[(i + 1) % TABS.length].key;
    if (e.key === 'ArrowLeft') next = TABS[(i + TABS.length - 1) % TABS.length].key;
    if (next) {
      e.preventDefault();
      setTab(next);
      tabRefs.current[next]?.focus();
    }
  };

  return (
    <section id="discover" className="showcase bg-gradient-to-b from-white via-purple-50/40 to-white py-16 sm:py-24" data-testid="home-showcase">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="max-w-2xl mb-10 sm:mb-14">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-purple-600 mb-3" data-testid="home-showcase-eyebrow">Explore Recalibrate</p>
          <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-bold text-[#111318] leading-[1.08] mb-4" style={{ textWrap: 'balance' }} data-testid="home-showcase-title">
            Learn how your body works, <span className="brand-flow bg-gradient-to-r from-[#4d8df7] via-[#7c6cf7] to-[#9b44f0] bg-clip-text text-transparent">then track what it does.</span>
          </h2>
          <p className="text-base sm:text-lg text-[#545b67] leading-relaxed">
            Guides you can keep, free research portals for real conditions, and the articles behind it all. The app is where it comes together.
          </p>
        </div>

        <div className="grid gap-8 lg:gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] items-start">
          {/* The app: the main action of the section */}
          <div className="bg-white rounded-3xl border border-purple-100 shadow-[0_20px_60px_-30px_rgba(124,58,237,0.35)] p-6 sm:p-8 lg:sticky lg:top-24" data-testid="home-showcase-app">
            <div className="flex gap-5 sm:gap-6 items-center">
              <div className="w-[110px] sm:w-[132px] shrink-0 rounded-[22px] border-[5px] border-[#111318] overflow-hidden shadow-[0_24px_48px_-24px_rgba(124,58,237,0.55)]">
                <SmartImage src="/app-previews/store-1-720w.webp" alt="Recalibrate app home screen" width={720} height={1280} className="w-full h-auto block" sizes="132px" />
              </div>
              <div className="min-w-0">
                <span className="inline-block rounded-full bg-amber-300 text-[#1c1917] text-[10px] font-extrabold uppercase tracking-[0.12em] px-2.5 py-1 mb-3">7 days free</span>
                <h3 className="text-xl sm:text-2xl font-bold text-[#111318] leading-tight mb-2">The Recalibrate app</h3>
                <p className="text-sm text-[#545b67] leading-relaxed">Log how you feel in a minute a day, see the patterns behind flares, and share a clear report with your clinician.</p>
              </div>
            </div>
            <div className="mt-6 flex flex-col gap-4">
              <a
                href={appUrl('/', 'showcase-start-free', 'home')}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-showcase-start-free"
                className="group inline-flex items-center justify-center gap-2 cta-shimmer rounded-2xl bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3.5 font-bold hover:shadow-xl hover:shadow-purple-600/30 hover:-translate-y-0.5 transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7c3aed]"
              >
                Start free <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </a>
              <StoreBadges align="center" label={false} web={false} testidPrefix="showcase-store" />
              <p className="text-[11px] text-[#8a909b] text-center">Then Go $12.99 or Pro $34.99 a month. Cancel anytime.</p>
            </div>
          </div>

          {/* One shelf, three sets */}
          <div className="min-w-0">
            <div role="tablist" aria-label="Browse Recalibrate resources" className="flex flex-wrap gap-2 mb-5" onKeyDown={onKeyDown}>
              {TABS.map((t) => {
                const active = tab === t.key;
                return (
                  <button
                    key={t.key}
                    ref={(el) => { tabRefs.current[t.key] = el; }}
                    role="tab"
                    id={`showcase-tab-${t.key}`}
                    aria-selected={active}
                    aria-controls={`showcase-panel-${t.key}`}
                    tabIndex={active ? 0 : -1}
                    onClick={() => setTab(t.key)}
                    data-testid={`home-showcase-tab-${t.key}`}
                    className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7c3aed] ${
                      active ? 'bg-gradient-to-r from-blue-500 to-purple-600 border-transparent text-white' : 'bg-white border-[#e4e6eb] text-[#545b67] hover:border-[#c9ccd3] hover:text-[#111318]'
                    }`}
                  >
                    {t.label}
                    <span className={`ml-1.5 tabular-nums ${active ? 'text-white/60' : 'text-[#8a909b]'}`}>{t.count}</span>
                  </button>
                );
              })}
            </div>

            {/* Guides */}
            <div role="tabpanel" id="showcase-panel-guides" aria-labelledby="showcase-tab-guides" hidden={tab !== 'guides'} data-testid="home-showcase-panel-guides">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                {GUIDES.map((g) => {
                  const Tag = g.comingSoon ? 'div' : g.to ? Link : 'a';
                  const props = g.comingSoon ? {} : g.to ? { to: g.to } : { href: getProductEtsyUrl(g.id), target: '_blank', rel: 'noopener noreferrer' };
                  return (
                    <Tag
                      key={g.id}
                      {...props}
                      data-testid={`home-showcase-guide-${g.id}`}
                      className={`group flex flex-col bg-white rounded-2xl border border-[#e4e6eb] overflow-hidden transition-all ${
                        g.comingSoon ? 'opacity-80' : 'hover:border-[#c9ccd3] hover:-translate-y-0.5 hover:shadow-[0_16px_32px_-20px_rgba(17,19,24,0.45)]'
                      }`}
                    >
                      <div className="relative aspect-[13/16] bg-[#eceef2] flex items-center justify-center p-3">
                        <SmartImage src={g.cover} alt={`${g.title} cover`} width={520} height={680} sizes="(max-width: 640px) 45vw, 220px" className="max-h-full w-auto rounded-md shadow-[0_10px_24px_-12px_rgba(17,19,24,0.5)]" />
                        {g.comingSoon && <span className="absolute top-2.5 right-2.5 rounded-full bg-[#111318] text-white text-[9px] font-bold uppercase tracking-[0.12em] px-2 py-0.5">Soon</span>}
                      </div>
                      <div className="p-3 sm:p-4 flex flex-col gap-1 flex-1">
                        <h3 className="text-[13px] sm:text-sm font-bold text-[#111318] leading-snug line-clamp-2">{g.title}</h3>
                        <div className="mt-auto pt-2 flex items-center justify-between text-xs text-[#8a909b]">
                          <span>{g.kind}</span>
                          <span className={g.comingSoon ? '' : 'text-[15px] font-extrabold text-[#111318] tabular-nums'}>{g.comingSoon ? 'In the works' : g.price}</span>
                        </div>
                      </div>
                    </Tag>
                  );
                })}
              </div>
              <div className="mt-5 flex flex-wrap items-center justify-between gap-2 text-sm text-[#8a909b]">
                <span>Instant PDF downloads on Etsy, selected titles on Amazon</span>
                <Link to="/shop" className="font-bold text-[#7c3aed] inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid="home-showcase-shop-link">Visit the shop <ArrowRight className="w-4 h-4" /></Link>
              </div>
            </div>

            {/* Portals */}
            <div role="tabpanel" id="showcase-panel-portals" aria-labelledby="showcase-tab-portals" hidden={tab !== 'portals'} data-testid="home-showcase-panel-portals">
              <div className="grid sm:grid-cols-2 gap-3">
                {HOME_PORTALS.map((p) => (
                  <a key={p.slug} href={p.hub} data-testid={`home-showcase-portal-${p.slug}`} className="group flex items-center gap-3 bg-white rounded-2xl border border-[#e4e6eb] p-2.5 pr-4 hover:border-[#c9ccd3] transition-colors min-w-0">
                    <SmartImage src={p.image} alt="" width={160} height={108} sizes="80px" className="w-20 h-14 object-cover rounded-xl shrink-0" />
                    <span className="min-w-0 flex-1">
                      <strong className="block text-sm text-[#111318] font-bold">{p.name}</strong>
                      <span className="block text-xs text-[#8a909b] truncate">{p.tagline}</span>
                    </span>
                    <ArrowUpRight className="w-4 h-4 text-[#8a909b] group-hover:text-[#7c3aed] shrink-0 transition-colors" />
                  </a>
                ))}
              </div>
              <div className="mt-5 flex flex-wrap items-center justify-between gap-2 text-sm text-[#8a909b]">
                <span>Free, no account needed. Education, not a diagnosis.</span>
                <Link to="/portals" className="font-bold text-[#7c3aed] inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid="home-showcase-portals-link">All portals <ArrowRight className="w-4 h-4" /></Link>
              </div>
            </div>

            {/* Articles */}
            <div role="tabpanel" id="showcase-panel-articles" aria-labelledby="showcase-tab-articles" hidden={tab !== 'articles'} data-testid="home-showcase-panel-articles">
              <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                {ARTICLES.map((post) => (
                  <Link key={post.slug} to={`/blog/${post.slug}`} data-testid={`home-showcase-article-${post.id}`} className="group flex flex-col bg-white rounded-2xl border border-[#e4e6eb] overflow-hidden hover:border-[#c9ccd3] transition-colors">
                    <div className="aspect-[16/9] overflow-hidden bg-[#eceef2]">
                      <SmartImage src={post.image} alt={post.imageAlt || post.title} width={post.imageWidth || 800} height={post.imageHeight || 500} sizes="(max-width: 640px) 90vw, 340px" className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500" />
                    </div>
                    <div className="p-4 flex flex-col gap-1.5 flex-1">
                      <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#7c3aed]">{normaliseCategory(post.category)}</span>
                      <h3 className="text-[15px] font-bold text-[#111318] leading-snug line-clamp-3">{post.title}</h3>
                      <span className="mt-auto pt-1 flex items-center gap-1.5 text-xs text-[#8a909b]"><Clock className="w-3 h-3" />{post.readTime}</span>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="mt-5 flex flex-wrap items-center justify-between gap-2 text-sm text-[#8a909b]">
                <span>Written by Tristan Siokos</span>
                <Link to="/blog" className="font-bold text-[#7c3aed] inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid="view-all-blog-btn">All articles <ArrowRight className="w-4 h-4" /></Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
